import {
  ConnectedPosition,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { Directive, Input, TemplateRef, inject } from '@angular/core';

/*
 * What app-select and app-multi-select share: reading labels and values of
 * the options, the search and the drop down on a CDK overlay.
 */
// Based on PrimeNG 14 (MIT), see tools/licenses/primeng.txt.

/** A template of a select, e.g. `<ng-template appSelectTemplate="group">`. */
@Directive({ selector: 'ng-template[appSelectTemplate]', standalone: true })
export class SelectTemplateDirective {
  @Input('appSelectTemplate') name = '';
  template = inject<TemplateRef<unknown>>(TemplateRef);
}

/** The keys of the options, as the inputs of the selects name them. */
export interface OptionFields {
  optionLabel?: string;
  optionValue?: string;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
}

/** An entry of the list: a group header or an option that can be chosen. */
export interface SelectEntry {
  group: boolean;
  option: unknown;
  label: string;
  /** The position among the options that can be chosen, -1 for groups. */
  index: number;
}

/** The value of a field of an object, also nested as in 'a.b'. */
export const resolveField = (data: unknown, field?: string): unknown => {
  if (!field || data === null || typeof data !== 'object') {
    return undefined;
  }
  return field
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value !== null && typeof value === 'object'
          ? (value as Record<string, unknown>)[key]
          : undefined,
      data
    );
};

const field = (option: unknown, key: string) =>
  option !== null && typeof option === 'object'
    ? (option as Record<string, unknown>)[key]
    : undefined;

export const optionLabel = (option: unknown, fields: OptionFields): string => {
  const label = fields.optionLabel
    ? resolveField(option, fields.optionLabel)
    : field(option, 'label') !== undefined
      ? field(option, 'label')
      : option;
  return label === null || label === undefined ? '' : String(label);
};

export const optionValue = (option: unknown, fields: OptionFields): unknown =>
  fields.optionValue
    ? resolveField(option, fields.optionValue)
    : fields.optionLabel || field(option, 'value') === undefined
      ? option
      : field(option, 'value');

export const groupLabel = (group: unknown, fields: OptionFields): string => {
  const label = fields.optionGroupLabel
    ? resolveField(group, fields.optionGroupLabel)
    : field(group, 'label');
  return label === null || label === undefined ? '' : String(label);
};

export const groupChildren = (
  group: unknown,
  fields: OptionFields
): unknown[] => {
  const children = fields.optionGroupChildren
    ? resolveField(group, fields.optionGroupChildren)
    : field(group, 'items');
  return Array.isArray(children) ? children : [];
};

/** The options that can be chosen, without the groups. */
export const flatOptions = (
  options: unknown[] | null | undefined,
  group: boolean,
  fields: OptionFields
): unknown[] =>
  (options || []).flatMap((option) =>
    group ? groupChildren(option, fields) : [option]
  );

// The search: 'contains', without case and accents.
const normalize = (text: unknown) =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase();

/**
 * Whether an option matches the search text. `filterBy` names the fields,
 * separated by commas; without it the label is searched.
 */
export const matchesFilter = (
  option: unknown,
  filter: string,
  filterBy: string | undefined,
  fields: OptionFields
) => {
  const text = normalize(filter.trim());
  if (!text) {
    return true;
  }
  const values = filterBy
    ? filterBy.split(',').map((key) => resolveField(option, key.trim()))
    : [optionLabel(option, fields)];
  return values.some((value) => normalize(value).includes(text));
};

/** The list the panel shows: groups with their options, filtered. */
export const selectEntries = (
  options: unknown[] | null | undefined,
  group: boolean,
  filter: string,
  filterBy: string | undefined,
  fields: OptionFields
): SelectEntry[] => {
  const entries: SelectEntry[] = [];
  let index = 0;
  const add = (option: unknown) =>
    entries.push({
      group: false,
      option,
      label: optionLabel(option, fields),
      index: index++,
    });
  (options || []).forEach((option) => {
    if (!group) {
      if (matchesFilter(option, filter, filterBy, fields)) {
        add(option);
      }
      return;
    }
    const children = groupChildren(option, fields).filter((child) =>
      matchesFilter(child, filter, filterBy, fields)
    );
    if (children.length) {
      entries.push({
        group: true,
        option,
        label: groupLabel(option, fields),
        index: -1,
      });
      children.forEach(add);
    }
  });
  return entries;
};

// Below the field and left aligned; above when there is no room below, right
// aligned when there is none to the right.
const positions: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
];

/**
 * The overlay of a drop down at its field, at least as wide as the field.
 * Scrolling the page closes it.
 */
export const createSelectOverlay = (
  overlay: Overlay,
  origin: HTMLElement
): OverlayRef =>
  overlay.create({
    positionStrategy: overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false),
    scrollStrategy: overlay.scrollStrategies.close(),
    minWidth: origin.getBoundingClientRect().width,
  });

let nextId = 0;
/** An id for the list of a select, for aria-controls. */
export const selectListId = () => `app-select-list-${nextId++}`;
